import { Box, Tab, Tabs } from '@mui/material';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IBookingRequestSubmission } from '../../../../library/models/publicBooking';
import ResourcesIds from '../../../../resources/ResourcesIds';
import { ExistingAccountForm } from './ExistingAccountForm';
import { tabsStyles } from './finishStep.styles';
import { BookingChoice, SignupForm } from './SignupForm';

interface FinishStepProps {
    clinicName: string;
    booking: BookingChoice;
    /** A new visitor's request went off for confirmation by email. */
    onSent: (submission: IBookingRequestSubmission) => void;
    /** An existing patient signed in; the wizard now books as them. */
    onSignedIn: () => void;
}

/**
 * The last step, and the only one that asks who the visitor is.
 *
 * Two doors rather than one form that guesses: a new visitor gives their details and confirms
 * by email, an existing patient signs in and books at once. Nothing on either door says
 * whether an address already has an account -- the sign-in fails exactly as a wrong password
 * does, and the new-visitor door answers "check your inbox" whatever it finds, emailing a known
 * address how to finish rather than telling the screen.
 */
export const FinishStep = (props: FinishStepProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const [door, setDoor] = useState<'new' | 'existing'>('new');

    return (
        <Box>
            <Tabs value={door} onChange={(_, next) => setDoor(next)} sx={tabsStyles}>
                <Tab value="new" label={translate(ResourcesIds.PUBLIC_BOOKING_NEW_TAB)} />
                <Tab value="existing" label={translate(ResourcesIds.PUBLIC_BOOKING_EXISTING_TAB)} />
            </Tabs>

            {door === 'new' ? (
                <SignupForm booking={props.booking} onSent={props.onSent} />
            ) : (
                <ExistingAccountForm clinicName={props.clinicName} onSignedIn={props.onSignedIn} />
            )}
        </Box>
    );
};
